import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@heroui/react";
import { ChevronDown, User } from "lucide-react";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { truncateText } from "@/shared/utils/helperFunctions";

function getOptionSearchText(option) {
  return (
    option.searchText ??
    [option.title, option.description].filter(Boolean).join(" ")
  ).toLowerCase();
}

function getSubtitleParts(option) {
  if (option?.subtitleParts?.length >= 2) {
    return [option.subtitleParts[0] || "N/A", option.subtitleParts[1] || "N/A"];
  }
  if (option?.description?.includes(" · ")) {
    const [primary, secondary] = option.description.split(" · ");
    return [primary || "N/A", secondary || "N/A"];
  }
  return [option?.description || "N/A", "N/A"];
}

const DefaultTrigger = ({
  open,
  selectedOption,
  subtitlePrimary,
  subtitleSecondary,
  subtitleTruncateLength,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full max-w-full min-w-0 items-start gap-1 rounded-lg px-1 py-0.5 text-left"
    aria-expanded={open}
    aria-haspopup="listbox"
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1">
        <h5 className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">
          {selectedOption?.title || "N/A"}
        </h5>
        <ChevronDown
          size={14}
          className={`shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>
      <div className="mt-[3px] flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
        <span className="truncate">
          {truncateText(
            subtitlePrimary,
            subtitlePrimary.length > subtitleTruncateLength
              ? subtitleTruncateLength
              : subtitlePrimary.length,
          )}
        </span>
        <span className="h-1 w-1 shrink-0 rounded-full bg-gray-300" />
        <span className="truncate">
          {truncateText(
            subtitleSecondary,
            subtitleSecondary.length > subtitleTruncateLength
              ? subtitleTruncateLength
              : subtitleSecondary.length,
          )}
        </span>
      </div>
    </div>
  </button>
);

const UserAssignTrigger = ({
  open,
  selectedOption,
  placeholderTitle,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full items-center gap-2 border-b border-gray-300 dark:border-gray-700 pb-2 text-left cursor-pointer hover:border-secondary transition-colors"
    aria-expanded={open}
    aria-haspopup="listbox"
  >
    <div className="w-6 h-6 rounded-full flex items-center justify-center border border-dashed border-gray-300 dark:border-gray-700 flex-shrink-0">
      <User size={14} className="text-gray-500 dark:text-gray-400" strokeWidth={1.7} />
    </div>
    <span
      className={`text-[0.763rem] font-inter truncate ${
        selectedOption ? "text-gray-800 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"
      }`}
    >
      {selectedOption?.title || placeholderTitle}
    </span>
  </button>
);

const DefaultOptionRow = ({ option, isActive, onSelect }) => (
  <button
    type="button"
    role="option"
    aria-selected={isActive}
    onClick={onSelect}
    className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors ${
      isActive ? "bg-secondary/10 text-secondary dark:text-secondary-light" : "hover:bg-gray-100 dark:hover:bg-gray-800"
    }`}
  >
    <span
      className={`truncate text-xs font-medium ${
        isActive ? "text-secondary dark:text-secondary-light" : "text-gray-900 dark:text-gray-100"
      }`}
    >
      {option.title}
    </span>
    {option.description ? (
      <span className="truncate text-[10px] text-gray-500 dark:text-gray-400">
        {option.description}
      </span>
    ) : null}
  </button>
);

const UserOptionRow = ({ option, isActive, onSelect }) => (
  <button
    type="button"
    role="option"
    aria-selected={isActive}
    onClick={onSelect}
    className={`flex w-full items-center gap-2 px-4 py-2 text-left transition-colors ${
      isActive
        ? "bg-secondary/10 dark:bg-secondary/20"
        : "hover:bg-gray-100 dark:hover:bg-gray-800"
    }`}
  >
    <Avatar
      src={option.avatar}
      name={option.title}
      className={`w-7 h-7 flex-shrink-0 ${option.avatarClassName || ""}`}
    />
    <div className="min-w-0 flex flex-col">
      <span
        className={`truncate text-[0.75rem] font-normal font-inter ${
          isActive ? "text-secondary dark:text-secondary-light" : "text-gray-800 dark:text-gray-100"
        }`}
      >
        {option.title}
      </span>
      {option.description ? (
        <span className="truncate text-[0.65rem] font-normal text-gray-500 dark:text-gray-400 font-inter leading-tight">
          {option.description}
        </span>
      ) : null}
    </div>
  </button>
);

const SearchablePopoverSelect = ({
  options = [],
  selectedKey,
  onSelect,
  variant = "default",
  placeholderTitle = "Assign Name",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  className = "relative min-w-0 flex-1",
  popoverClassName = "",
  subtitleTruncateLength = 27,
  zIndex = "z-[100]",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.key) === String(selectedKey)),
    [options, selectedKey],
  );

  const [subtitlePrimary, subtitleSecondary] = getSubtitleParts(selectedOption);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) =>
      getOptionSearchText(option).includes(normalizedQuery),
    );
  }, [options, query]);

  const handleSelect = (key) => {
    onSelect?.(key);
    setOpen(false);
  };

  const toggleOpen = () => setOpen((prev) => !prev);

  const OptionRow = variant === "user" ? UserOptionRow : DefaultOptionRow;

  return (
    <div ref={containerRef} className={className}>
      {variant === "user" ? (
        <UserAssignTrigger
          open={open}
          selectedOption={selectedOption}
          placeholderTitle={placeholderTitle}
          onToggle={toggleOpen}
        />
      ) : (
        <DefaultTrigger
          open={open}
          selectedOption={selectedOption}
          subtitlePrimary={subtitlePrimary}
          subtitleSecondary={subtitleSecondary}
          subtitleTruncateLength={subtitleTruncateLength}
          onToggle={toggleOpen}
        />
      )}

      {open && (
        <div
          className={`absolute left-0 top-full ${zIndex} mt-1 overflow-hidden rounded-xl border border-secondary dark:border-secondary/80 bg-white dark:bg-gray-900 shadow-lg ${
            popoverClassName || "right-0"
          }`}
        >
          <div className="px-4 pt-4 pb-2.5">
            <SearchBar
              value={query}
              onChange={setQuery}
              setFilterValue={setQuery}
              placeholder={searchPlaceholder}
              className="w-full"
              debounceDelay={0}
            />
          </div>

          <ul
            className="max-h-[min(280px,40vh)] overflow-y-auto overscroll-contain"
            role="listbox"
          >
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-gray-500">
                {emptyMessage}
              </li>
            ) : (
              filteredOptions.map((option) => {
                const isActive = String(option.key) === String(selectedKey);
                return (
                  <li key={option.key}>
                    <OptionRow
                      option={option}
                      isActive={isActive}
                      onSelect={() => handleSelect(option.key)}
                    />
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchablePopoverSelect;
